import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [ 'input', 'pacContainer' ]
  static values = { buyPage: Boolean, rentalPage: Boolean, hosmanian: Boolean, woosmapKey: String, railsEnv: String, placeholderText: String }

  connect() {
    this.estimationKind = this.buyPageValue === true ? 'buy' : 'online';
    this.inputTarget.placeholder = this.placeholderTextValue || (this.estimationKind === 'buy' ? 'Entrez une ville, un arrondissement...' : 'Adresse du bien à estimer...');

    this.predictions = [];
    this.selectedIndex = -1;
    this.debounceTimer = null;

    this._handleInput = () => this.handleInput();
    this._handleKeyDown = (e) => this.handleKeyDown(e);
    this.initWoosmapScript().then(() => {
      this.localitiesService = new woosmap.map.LocalitiesService();
      this.inputTarget.addEventListener('input', this._handleInput);
      this.inputTarget.addEventListener('keydown', this._handleKeyDown);
    });

    window.addEventListener('click', this.closePredictions);
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', this._handleInput);
    this.inputTarget.removeEventListener('keydown', this._handleKeyDown);
    window.removeEventListener('click', this.closePredictions);
    clearTimeout(this.debounceTimer);
  }

  initWoosmapScript() {
    if (window.woosmap) return Promise.resolve();
    if (window._woosmapLoadingPromise) return window._woosmapLoadingPromise;

    // NOTE: local http workaround
    if (!window.crypto?.randomUUID && ['development', 'test'].includes(this.railsEnvValue)) {
      window.crypto.randomUUID = () =>
      ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c => (c ^ (Math.random() * 16 >> c / 4)).toString(16));
    }

    window._woosmapLoadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://sdk.woosmap.com/map/map.js?key=${this.woosmapKeyValue}`;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });

    return window._woosmapLoadingPromise;
  };

  handleInput() {
    const value = this.inputTarget.value;
    if (value.length < 2) {
      this.showPredictions([]);
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.localitiesService.autocomplete({
        input: value,
        types: this.buyPageValue ? ['locality'] : ['locality', 'address'],
        language: 'FR',
        components: { country: ['fr-fr'] },
      })
      .then(response => this.showPredictions(response.localities))
      .catch(error => console.error('Woosmap error:', error));
    }, 250);
  }

  handleKeyDown(event) {
    if (!this.predictions.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.predictions.length - 1);
        this.updateHighlight();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.updateHighlight();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0) this.selectPrediction(this.predictions[this.selectedIndex]);
        break;
    }
  }

  updateHighlight() {
    const items = this.pacContainerTarget.querySelectorAll('li');
    items.forEach((li, index) => {
      li.classList.toggle('bg-ih-purple-5', index === this.selectedIndex);
    });
  }

  showPredictions(localities) {
    this.predictions = localities;
    this.selectedIndex = -1;
    this.pacContainerTarget.innerHTML = '';

    if (!localities.length) return;

    const ul = document.createElement('ul');
    ul.className = 'absolute z-50 w-full bg-white rounded-2xl border border-ih-purple-20 overflow-hidden mt-1 top-full left-0';

    localities.forEach((prediction) => {
      const li = document.createElement('li');
      li.textContent = prediction.description;
      li.className = 'text-ih-indigo first:rounded-t-2xl last:rounded-b-2xl hover:bg-ih-purple-5 cursor-pointer select-none py-3 px-3 border-b border-b-ih-purple-10';
      li.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectPrediction(prediction);
      });
      ul.appendChild(li);
    });

    this.pacContainerTarget.appendChild(ul);
  }

  closePredictions = () => {
    this.showPredictions([]);
  }

  selectPrediction = async (prediction) => {
    this.showPredictions([]);
    try {
      const result = await this.localitiesService.getDetails({ publicId: prediction.public_id });
      this.place = result.result;
      this.inputTarget.value = this.place.formatted_address || prediction.description;
      this.redirectTo();
    } catch (error) {
      console.error('Woosmap getDetails error:', error);
    }
  }

  stopPropagation = (e) => {
    e.stopPropagation();
  }

  redirectTo = () => {
    if (this.buyPageValue === true) this.listingAdsUrl()
    else if (this.rentalPageValue === true) this.rentalFunnelUrl()
    else this.funnelUrl()
  }

  validePlaceData = () => {
    return this.place?.geometry?.location && this.place?.formatted_address && this.place?.address_components
  }

  listingAdsUrl = () => {
    if (this.place?.address_components) {
      const addressComponents = this.place.address_components;
      const postalCode = addressComponents.find(c => c.types.includes('postal_code'))?.short_name;
      const city = addressComponents.find(c => c.types.includes('locality'))?.short_name;
      if (city) {
        const zone = `${city} ${postalCode || ''}`;
        const params = new URLSearchParams({ zones: zone });
        window.location = `/annonces?${params.toString()}`;
        return;
      }
    }
    window.location = `/annonces`;
  }

  funnelUrl = () => {
    let fullPath;

    if (this.hosmanianValue) {
      fullPath = window.location.origin.replace(window.location.host, `admin.${window.location.host.replace('www.', '')}/nouveau-lead`)
    } else {
      fullPath = window.location.origin.replace(window.location.host, `app.${window.location.host.replace('www.', '')}/estimation`)
    }

    if (this.validePlaceData()) {
      this.dataToParams()
      window.location = `${fullPath}?${this.params}`;
    } else window.location = `${fullPath}`;
  }

  rentalFunnelUrl = () => {
    const fullPath = window.location.origin.replace(window.location.host, `app.${window.location.host.replace('www.', '')}/location`)

    if (this.validePlaceData()) {
      this.dataToParams()
      window.location = `${fullPath}?${this.params}`;
    } else window.location = `${fullPath}`;
  }

  dataToParams = () => {
    this.params = new URLSearchParams();
    this.params.append('formatted_address', this.place.formatted_address);
    this.params.append('lat', this.place.geometry.location.lat);
    this.params.append('lng', this.place.geometry.location.lng);
    for(let key in this.place.address_components) {
      const addressComponent = this.place.address_components[key];
      if (addressComponent.types.includes('street_number')) this.params.append('street_number', addressComponent['long_name'])
      else if (addressComponent.types.includes('route')) this.params.append('street_name', addressComponent['long_name'])
      else if (addressComponent.types.some(t => t.includes('postal_code'))) this.params.append('zip_code', addressComponent['long_name'])
      else if (addressComponent.types.includes('locality')) this.params.append('city', addressComponent['long_name'])
      else if (addressComponent.types.includes('country')) this.params.append('country_code', addressComponent['short_name'])
    }
  }
}
